import { useEffect, useState, useCallback } from "react";
import { ChevronLeft, ChevronRight, ExternalLink, ImageOff, ShieldCheck } from "lucide-react";
import { QRCodeSVG } from "qrcode.react";

// Slider di presentazione prodotti: immagine grande, nome in sovraimpressione, link scheda tecnica.
// Autoplay + controlli manuali (frecce/dots). In printPage mostra una griglia statica.
export function GaranzieBadges({ list, testid = "garanzia-badge" }) {
  if (!list || !list.length) return null;
  return (
    <div className="flex flex-wrap gap-1.5 mt-1.5">
      {list.map((g, i) => (
        <span key={i} className="inline-flex items-center gap-1 rounded-full border border-primary/40 bg-primary/10 px-2 py-0.5 text-[10.5px] font-medium text-primary" data-testid={testid}>
          <ShieldCheck className="h-3 w-3" strokeWidth={2} /> Garanzia {g}
        </span>
      ))}
    </div>
  );
}

export default function ProductSlider({ prodotti = [], printPage = false }) {
  const [idx, setIdx] = useState(0);
  const n = prodotti.length;

  const go = useCallback((next) => {
    setIdx((c) => (next + n) % n);
  }, [n]);

  useEffect(() => {
    if (printPage || n <= 1) return;
    const t = setInterval(() => setIdx((c) => (c + 1) % n), 5000);
    return () => clearInterval(t);
  }, [printPage, n]);

  if (n === 0) return null;

  const Slide = ({ p, tall }) => (
    <div className="overflow-hidden rounded-xl border border-border bg-card shadow-sm" data-testid="product-card">
      <div className={`relative bg-secondary/30 ${tall ? "aspect-[16/9]" : "aspect-video"}`}>
        {p.photo_url ? (
          <img src={p.photo_url} alt={p.nome} className="absolute inset-0 h-full w-full object-contain p-3" />
        ) : (
          <div className="absolute inset-0 flex items-center justify-center text-muted-foreground">
            <ImageOff className="h-10 w-10 opacity-50" strokeWidth={1.5} />
          </div>
        )}
      </div>
      <div className="p-4 sm:p-5 bg-card">
        <div className="text-[10px] uppercase tracking-[0.18em] text-muted-foreground">{p.categoria}</div>
        <div className="font-heading font-extrabold text-lg sm:text-xl text-foreground leading-tight">
          {p.brand ? `${p.brand} ` : ""}{p.nome}
        </div>
        {p.dettaglio && <div className="text-xs text-muted-foreground mt-0.5">{p.dettaglio}</div>}
        <GaranzieBadges list={p.garanzie} />
        {p.datasheet_url ? (
          <div className="mt-3 flex items-center gap-3">
            <a href={p.datasheet_url} target="_blank" rel="noopener noreferrer"
               data-testid={`slider-datasheet-${p.categoria}`}
               className="inline-flex items-center gap-1.5 rounded-full bg-[#FDD003] px-3 py-1.5 text-xs font-semibold text-black hover:bg-[#e6d600] transition-colors">
              <ExternalLink className="h-3.5 w-3.5" /> Scheda tecnica
            </a>
            <span className="shrink-0 rounded-md bg-white p-1 border border-border" data-testid={`slider-datasheet-qr-${p.categoria}`}>
              <QRCodeSVG value={p.datasheet_url} size={56} level="M" bgColor="#ffffff" fgColor="#1f1f1f" />
            </span>
            {p.descrizione && <p className="hidden sm:block flex-1 min-w-0 text-xs text-muted-foreground leading-relaxed whitespace-pre-line" data-testid={`slider-descrizione-${p.categoria}`}>{p.descrizione}</p>}
          </div>
        ) : (
          <span className="mt-3 inline-block text-[11px] text-muted-foreground">Scheda su richiesta</span>
        )}
        {p.descrizione && <p className={`${p.datasheet_url ? "sm:hidden " : ""}mt-3 text-xs text-muted-foreground leading-relaxed whitespace-pre-line`} data-testid={`slider-descrizione-m-${p.categoria}`}>{p.descrizione}</p>}
      </div>
    </div>
  );

  if (printPage) {
    return (
      <div className="grid sm:grid-cols-2 gap-3" data-testid="product-slider-print">
        {prodotti.map((p, i) => (
          <div key={i} className="flex gap-3 rounded-lg border border-border bg-card p-2.5 break-inside-avoid" data-testid="product-card">
            <div className="relative w-20 h-20 shrink-0 rounded-md bg-secondary/30 overflow-hidden">
              {p.photo_url ? (
                <img src={p.photo_url} alt={p.nome} className="absolute inset-0 h-full w-full object-contain p-1" />
              ) : (
                <div className="absolute inset-0 flex items-center justify-center text-muted-foreground"><ImageOff className="h-6 w-6 opacity-50" strokeWidth={1.5} /></div>
              )}
            </div>
            <div className="min-w-0 flex-1">
              <div className="text-[9px] uppercase tracking-[0.15em] text-muted-foreground">{p.categoria}</div>
              <div className="font-heading font-bold text-sm text-foreground leading-tight">{p.brand ? `${p.brand} ` : ""}{p.nome}</div>
              {p.dettaglio && <div className="text-[11px] text-muted-foreground mt-0.5">{p.dettaglio}</div>}
              <GaranzieBadges list={p.garanzie} />
              {p.descrizione && <div className="text-[10px] text-muted-foreground mt-1 leading-snug" data-testid="product-card-descrizione">{p.descrizione}</div>}
              {p.datasheet_url ? (
                <a href={p.datasheet_url} target="_blank" rel="noopener noreferrer"
                   data-testid={`slider-datasheet-${p.categoria}`}
                   className="mt-1.5 inline-flex items-center gap-1 rounded-full bg-[#FDD003] px-2 py-0.5 text-[10px] font-semibold text-black">
                  <ExternalLink className="h-3 w-3" /> Scheda tecnica
                </a>
              ) : (
                <span className="mt-1 inline-block text-[10px] text-muted-foreground">Scheda su richiesta</span>
              )}
            </div>
            {p.datasheet_url && (
              <span className="shrink-0 self-center rounded bg-white p-1 border border-border" data-testid={`slider-datasheet-qr-${p.categoria}`}>
                <QRCodeSVG value={p.datasheet_url} size={64} level="M" bgColor="#ffffff" fgColor="#1f1f1f" />
              </span>
            )}
          </div>
        ))}
      </div>
    );
  }

  const p = prodotti[idx];
  return (
    <div className="relative" data-testid="product-slider">
      <Slide p={p} tall />
      {n > 1 && (
        <>
          <button type="button" onClick={() => go(idx - 1)} aria-label="Prodotto precedente"
            data-testid="slider-prev"
            className="absolute left-3 top-[38%] -translate-y-1/2 grid h-9 w-9 place-items-center rounded-full bg-black/50 text-white backdrop-blur hover:bg-black/70 transition-colors">
            <ChevronLeft className="h-5 w-5" />
          </button>
          <button type="button" onClick={() => go(idx + 1)} aria-label="Prodotto successivo"
            data-testid="slider-next"
            className="absolute right-3 top-[38%] -translate-y-1/2 grid h-9 w-9 place-items-center rounded-full bg-black/50 text-white backdrop-blur hover:bg-black/70 transition-colors">
            <ChevronRight className="h-5 w-5" />
          </button>
          <div className="mt-3 flex items-center justify-center gap-2" data-testid="slider-dots">
            {prodotti.map((_, i) => (
              <button key={i} type="button" onClick={() => setIdx(i)} aria-label={`Vai al prodotto ${i + 1}`}
                data-testid={`slider-dot-${i}`}
                className={`h-2 rounded-full transition-all ${i === idx ? "w-6 bg-primary" : "w-2 bg-border hover:bg-primary/50"}`} />
            ))}
          </div>
        </>
      )}
    </div>
  );
}
